import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { Solution } from "@/hooks/use-solutions";
import { cn } from "@/lib/utils";

const PHASE: Record<
  Solution["stage"],
  { eyebrow: string; description: string; tone: string; index: 0 | 1 | 2 }
> = {
  sales: {
    eyebrow: "Pre-sale",
    description: "Proposal & SOW intake",
    tone: "bg-[#F1ECFB] text-[#5E3FB0] dark:bg-[#1a0e2e] dark:text-[#b89df0]",
    index: 0,
  },
  discovery: {
    eyebrow: "Discovery",
    description: "Active scope decisions",
    tone: "bg-nymbl-sky text-nymbl-navy",
    index: 1,
  },
  build: {
    eyebrow: "Live build",
    description: "Change requests after launch",
    tone: "bg-success-bg text-success",
    index: 2,
  },
};

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);
  if (sec < 60) return "just now";
  if (min < 60) return `${min}m ago`;
  if (hr < 24) return `${hr}h ago`;
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return mo < 12 ? `${mo}mo ago` : `${Math.round(mo / 12)}y ago`;
}

export function SolutionCard({ solution }: { solution: Solution }) {
  const phase = PHASE[solution.stage];

  return (
    <Link
      to="/s/$solutionId/intake"
      params={{ solutionId: solution.id }}
      className={cn(
        "group rounded-xl border border-[var(--color-border)] bg-card p-6 transition-all",
        "hover:-translate-y-0.5 hover:border-nymbl-blue hover:shadow-md",
        "flex flex-col gap-4 min-h-[220px]",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            {solution.clientName}
          </div>
          <h3 className="font-display text-xl font-semibold text-foreground mt-1 leading-snug">
            {solution.name}
          </h3>
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold shrink-0",
            phase.tone,
          )}
          title={phase.description}
        >
          {phase.eyebrow}
        </span>
      </div>

      {solution.summary && (
        <p className="text-sm text-foreground/70 line-clamp-3">{solution.summary}</p>
      )}

      <PhaseProgress index={phase.index} />

      <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground pt-1">
        <div className="flex items-center gap-3">
          {solution.baselineVersion && (
            <span className="font-mono tabular-nums text-[11px]">
              Baseline {solution.baselineVersion}
            </span>
          )}
          <span>Updated {relativeTime(solution.lastActivity)}</span>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-nymbl-blue" />
      </div>
    </Link>
  );
}

function PhaseProgress({ index }: { index: 0 | 1 | 2 }) {
  const steps = ["Pre-sale", "Discovery", "Live build"] as const;
  return (
    <div className="flex items-center gap-1.5" role="progressbar" aria-label="Phase progress">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center gap-1.5 flex-1">
          <span
            className={cn("h-1.5 flex-1 rounded-full", i <= index ? "bg-nymbl-navy" : "bg-muted")}
          />
          {i < steps.length - 1 && <span className="h-1 w-1" aria-hidden />}
        </div>
      ))}
    </div>
  );
}
